import axios, { AxiosError, AxiosResponse } from 'axios';

import { DEFAULT_ERROR_RESPONSE } from '../constants';
import { HTTPResponse } from './../models';
import { combineQuery } from '../helpers';
import { QueryModel } from './../models';
import { getTraceparentHeader } from './launch-telemetry';

export const http = axios.create({
  withCredentials: true,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-type': 'application/json'
  },
  paramsSerializer: {
    serialize: (params: QueryModel) => {
      return combineQuery(params).slice(1);
    }
  }
});

const onFulfilled = (res: AxiosResponse<HTTPResponse<any>>) => {
  if (res.config?.skipInterceptor) {
    // case: if true, bypass the interceptor logic and just return the response
    return res.data;
  }
  if (res.data.errorMessage) {
    // case: successful request but have an error message
    throw res.data;
  }
  // case: everything is correct
  return res.data.result;
};

const isAborted = (error: AxiosError): boolean =>
  axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED';

const onRejected = (error: AxiosError) => {
  // Cancelled because the page is going away, not failed. This was 726 fake launch errors a day.
  if (isAborted(error)) {
    const aborted = { ...DEFAULT_ERROR_RESPONSE, errorMessage: '', aborted: true };
    if (error.config?.skipInterceptor) return aborted;
    throw aborted;
  }

  if (error.config?.skipInterceptor) {
    // Raw envelope for callers that read error codes themselves. A request that never got an HTTP response
    // (offline, DNS, CORS, blocked) has no envelope: hand back the default one rather than resolve to undefined,
    // which made every `response.result` read a TypeError (launch/info at boot, Token/refresh).
    return error.response?.data ?? { ...DEFAULT_ERROR_RESPONSE, noResponse: true };
  }

  throw error.response?.data || DEFAULT_ERROR_RESPONSE;
};

http.interceptors.response.use(onFulfilled, onRejected);

// Joins every API call to the launch trace; the backend honours W3C traceparent by default.
http.interceptors.request.use(config => {
  try {
    const traceparent = getTraceparentHeader();
    if (traceparent && config.headers) {
      config.headers['traceparent'] = traceparent;
    }
  } catch {
    /* telemetry must never block an API call */
  }
  return config;
});

let requestInterceptorId: number | null = null;

export const setupRequestInterceptor = (accessToken: string) => {
  // 1. If an interceptor already exists, remove it
  if (requestInterceptorId !== null) {
    http.interceptors.request.eject(requestInterceptorId);
  }
  // 2. Create the new interceptor and store its ID
  requestInterceptorId = http.interceptors.request.use(
    config => {
      if (config.headers) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );
};

